/**
 * useEnrollmentDetail Hook
 *
 * 수강 상세 정보를 조회하는 React Query hook입니다.
 *
 * 흐름:
 * 1. enrollmentId를 받아서 getEnrollmentDetail API 호출
 * 2. React Query가 자동으로 로딩, 에러, 데이터 상태 관리
 * 3. 캐싱을 통해 동일한 수강 정보 조회 시 재요청 방지 (5분)
 * 4. 컴포넌트에서 { data, isLoading, isError, error } 구조로 사용
 *
 * 캐싱 전략:
 * - staleTime: 5분 동안 데이터를 fresh 상태로 유지
 * - retry: 실패 시 1번만 재시도
 * - refetchOnWindowFocus: 윈도우 포커스 시 재조회 비활성화
 *
 * 사용 예:
 * ```typescript
 * const { data: enrollment, isLoading, isError } = useEnrollmentDetail(enrollmentId);
 * if (isLoading) return <LoadingSpinner />;
 * if (isError) return <ErrorMessage />;
 * return <div>{enrollment.course.title}</div>;
 * ```
 */

import { useQuery } from "@tanstack/react-query";
import { getEnrollmentDetail } from "../api/enrollmentApi";

/**
 * @param enrollmentId - 조회할 수강 ID
 * @returns React Query 결과 객체
 *   - data: EnrollmentDetailDto | undefined - 수강 상세 정보
 *   - isLoading: boolean - 로딩 상태
 *   - isError: boolean - 에러 발생 여부
 *   - error: Error | null - 에러 객체
 *   - refetch: () => void - 수동 재조회 함수
 */
export const useEnrollmentDetail = (enrollmentId: number) => {
  return useQuery({
    // queryKey: 캐시 식별자 (enrollmentId가 다르면 별도로 캐싱)
    queryKey: ["enrollment", enrollmentId],

    // queryFn: 실제 데이터를 가져오는 함수
    queryFn: () => getEnrollmentDetail(enrollmentId),

    // staleTime: 데이터가 fresh 상태로 유지되는 시간 (5분)
    // 이 시간 동안은 동일한 쿼리를 재호출해도 캐시된 데이터 반환
    staleTime: 5 * 60 * 1000,

    // retry: 실패 시 재시도 횟수 (1번만 재시도)
    retry: 1,

    // refetchOnWindowFocus: 윈도우 포커스 시 자동 재조회 비활성화
    // 수강 상세 정보는 자주 변하지 않으므로 불필요한 재조회 방지
    refetchOnWindowFocus: false,
  });
};

